/**
 * hub-session.service.js
 *
 * Gerencia a sessão do usuário recebida via postMessage do Hub.
 * O Hub envia o access_token_hub no sessionStorage com a estrutura:
 *   { accessToken: "JWT" }
 *
 * O JWT contém roles, perms e dados do usuário.
 */

const SESSION_KEY = 'access_token_hub';

const REQUIRED_PERMISSION = 'AGENCIA_CONTA_VIEW';
const ADMIN_ROLES = ['ADMINISTRADOR', 'SYSTEM_ADMIN'];

// ─── JWT ──────────────────────────────────────────────────────────────────────

/**
 * Decodifica o payload de um JWT sem verificar assinatura.
 * @param {string} token
 * @returns {object|null}
 */
function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// ─── Sessão ───────────────────────────────────────────────────────────────────

/**
 * Retorna o objeto bruto salvo no sessionStorage.
 * @returns {{ accessToken: string }|null}
 */
function getRawSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Salva o objeto de sessão no sessionStorage.
 * @param {{ accessToken: string }} session
 */
function saveSession(session) {
  try {
    clearSession(SESSION_KEY); // Limpa sessão anterior
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('[HubSession] Erro ao salvar sessão:', error);
  }
}

/**
 * Limpa a sessão do sessionStorage.
 */
function clearSession() {
  sessionStorage.removeItem(SESSION_KEY);
}

// ─── Payload do usuário ───────────────────────────────────────────────────────

/**
 * Retorna o payload decodificado do JWT ou null.
 * @returns {object|null}
 */
function getSessionPayload() {
  const session = getRawSession();
  if (!session?.accessToken) return null;
  return decodeJwt(session.accessToken);
}

/**
 * Retorna o objeto completo da sessão do usuário com dados úteis já extraídos.
 * Use este método nas páginas para obter os dados do usuário logado.
 *
 * @returns {{
 *   accessToken: string,
 *   sub: string,
 *   email: string,
 *   name: string,
 *   picture: string,
 *   roles: string[],
 *   perms: string[],
 *   instances: { name: string, code: string }[],
 *   exp: number,
 *   iat: number,
 *   firebase: object
 * }|null}
 */
function getSession() {
  const raw = getRawSession();
  const payload = getSessionPayload();

  if (!raw?.accessToken || !payload) return null;

  return {
    accessToken: raw.accessToken,
    sub: payload.sub || '',
    email: payload.email || payload.firebase?.email || '',
    name: payload.firebase?.name || '',
    picture: payload.firebase?.picture || '',
    roles: payload.roles || [],
    perms: payload.perms || [],
    instances: payload.instances || [],
    exp: payload.exp || 0,
    iat: payload.iat || 0,
    firebase: payload.firebase || {},
  };
}

// ─── Permissões ───────────────────────────────────────────────────────────────

/**
 * Verifica se o usuário possui a permissão informada.
 * @param {string} permission
 * @returns {boolean}
 */
function hasPermission(permission) {
  const session = getSession();
  return session?.perms?.includes(permission) ?? false;
}

/**
 * Verifica se o usuário possui pelo menos uma das roles informadas.
 * @param {string[]} roles
 * @returns {boolean}
 */
function hasAnyRole(roles = []) {
  const session = getSession();
  return roles.some((role) => session?.roles?.includes(role));
}

/**
 * Verifica se o JWT ainda está válido (não expirou).
 * @returns {boolean}
 */
function isTokenValid() {
  const payload = getSessionPayload();
  if (!payload?.exp) return false;
  return Date.now() / 1000 < payload.exp;
}

// ─── Acesso à página ──────────────────────────────────────────────────────────

/**
 * Verifica se o usuário tem acesso à Agência Virtual.
 * Regra: precisa ter AGENCIA_CONTA_VIEW OU ser ADMINISTRADOR/SYSTEM_ADMIN.
 *
 * @returns {{ allowed: boolean, reason: string }}
 */
function checkAccess() {
  const session = getSession();

  if (!session) {
    return { allowed: false, reason: 'Sessão não encontrada. Faça login novamente.' };
  }

  if (!isTokenValid()) {
    return { allowed: false, reason: 'Sessão expirada. Faça login novamente.' };
  }

  const isAdmin = hasAnyRole(ADMIN_ROLES);
  const hasPerm = hasPermission(REQUIRED_PERMISSION);

  if (!isAdmin && !hasPerm) {
    return {
      allowed: false,
      reason: `Acesso negado. Você não possui a permissão "${REQUIRED_PERMISSION}" ou o papel de Administrador.`,
    };
  }

  return { allowed: true, reason: '' };
}

// ─── Listener persistente ─────────────────────────────────────────────────────

let _listenerInitialized = false;

/**
 * Inicializa um listener global permanente que atualiza a sessão sempre que
 * o Hub enviar um novo SET_ACCESS_TOKEN, sem remover o listener após o primeiro evento.
 * Deve ser chamado uma única vez na inicialização da aplicação.
 */
function initSessionListener() {
  if (_listenerInitialized) return;
  _listenerInitialized = true;

  window.addEventListener('message', (event) => {
    if (event.data?.type === 'SET_ACCESS_TOKEN') {
      saveSession(event.data.message);
    }
  });
}

// ─── Export ───────────────────────────────────────────────────────────────────

export const HubSessionService = {
  saveSession,
  clearSession,
  getSession,
  getSessionPayload,
  hasPermission,
  hasAnyRole,
  isTokenValid,
  checkAccess,
  initSessionListener,
};
